/**
 * Editorial Box COVA - JavaScript
 * Author: Nicola Alessi - Bonsay
 */

class EditorialBoxCova {
  constructor(section) {
    this.section = section;
    this.mode = section.dataset.mode;

    if (this.mode === 'animated') {
      // Modalità animata
      this.overlay = section.querySelector('.editorial-box-cova__overlay');
      this.content = section.querySelector('.editorial-box-cova__text-wrapper');
      this.container = section.querySelector('.editorial-box-cova__container');
      this.init();
    } else if (this.mode === 'simple') {
      // Modalità semplice con parallasse
      this.media = section.querySelector('.editorial-box-cova__media');
      this.container = section.querySelector('.editorial-box-cova__container');
      this.initParallax();
    }
  }

  init() {
    // Usa Intersection Observer per rilevare quando la sezione è visibile
    this.setupIntersectionObserver();
    
    // Aggiungi scroll listener per l'animazione progressiva
    this.setupScrollListener();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.handleScroll();
        }
      });
    }, options);

    this.observer.observe(this.section);
  }

  setupScrollListener() {
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Trigger iniziale
    this.handleScroll();
  }

  handleScroll() {
    const rect = this.section.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const sectionHeight = rect.height;

    // Nuova logica a 2 fasi:
    // Fase 1 (0-0.5): Immagine diventa completamente visibile (overlay appare)
    // Fase 2 (0.5-1): Testo appare e sale fino alla posizione finale
    let scrollProgress = 0;

    if (rect.top <= windowHeight && rect.bottom >= 0) {
      // La sezione è visibile nella viewport

      // Calcola il progresso totale basato sulla posizione della sezione
      // Quando top = windowHeight: progress = 0 (sezione appena entra)
      // Quando top = 0: progress = 0.5 (immagine completamente visibile)
      // Quando top = -sectionHeight/2: progress = 1 (testo completamente visibile e centrato)

      const totalScrollRange = windowHeight + (sectionHeight * 0.5);
      const currentScroll = windowHeight - rect.top;
      scrollProgress = Math.max(0, Math.min(1, currentScroll / totalScrollRange));

    } else if (rect.bottom < 0) {
      // La sezione è completamente sopra la viewport
      scrollProgress = 1;
    }

    // Applica l'animazione basata sul progresso
    this.applyAnimation(scrollProgress);
  }

  applyAnimation(progress) {
    // Clamp progress tra 0 e 1
    progress = Math.max(0, Math.min(1, progress));

    // FASE 1 (progress 0 → 0.5): Overlay appare progressivamente
    // L'immagine diventa completamente visibile
    if (this.overlay) {
      // Overlay va da 0 a 1 nella prima metà del progresso (0 → 0.5)
      const overlayProgress = Math.max(0, Math.min(1, progress / 0.5));
      const overlayOpacity = overlayProgress;
      this.overlay.style.opacity = overlayOpacity;

      if (overlayOpacity > 0) {
        this.overlay.classList.add('is-visible');
      } else {
        this.overlay.classList.remove('is-visible');
      }
    }

    // FASE 2 (progress 0.5 → 1): Testo appare e sale
    // Il testo inizia ad apparire SOLO quando l'immagine è completamente visibile
    if (this.content) {
      // Il testo inizia quando progress > 0.5 (immagine completamente visibile)
      // Completa quando progress = 1
      const contentProgress = Math.max(0, Math.min(1, (progress - 0.5) / 0.5));

      // Opacità: da 0 a 1 con easing
      const contentOpacity = this.easeOutCubic(contentProgress);

      // Movimento: da 400px in basso a 0 (posizione finale centrata)
      // Usa easing per un movimento più fluido
      const translateY = 400 * (1 - this.easeOutCubic(contentProgress));

      this.content.style.opacity = contentOpacity;
      this.content.style.transform = `translateY(${translateY}px)`;

      if (contentProgress > 0) {
        this.content.classList.add('is-visible');
      } else {
        this.content.classList.remove('is-visible');
      }
    }
  }

  // Easing function per un movimento più fluido
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  // ============================================
  // PARALLAX MODE (Simple Mode)
  // ============================================
  initParallax() {
    // Leggi il setting dal data attribute
    const enableParallax = this.section.dataset.enableParallax === 'true';

    if (!enableParallax) {
      // Se il parallasse è disabilitato:
      // 1. NON aggiungere listener di scroll
      // 2. Assicurati che non ci siano transform applicati
      if (this.media) {
        this.media.style.transform = '';
        this.media.style.willChange = '';
        this.media.style.transition = '';
      }

      console.log('[Editorial Box] Parallasse disattivato - nessun listener di scroll attivo');
      return;
    }

    console.log('[Editorial Box] Parallasse attivato - listener di scroll attivo');
    this.setupParallaxScroll();
  }

  setupParallaxScroll() {
    // Questa funzione viene chiamata SOLO se enableParallax === true
    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleParallaxScroll();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Trigger iniziale
    this.handleParallaxScroll();
  }

  handleParallaxScroll() {
    // Questa funzione viene chiamata SOLO se enableParallax === true
    const rect = this.section.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Calcola se la sezione è visibile
    if (rect.top <= windowHeight && rect.bottom >= 0) {
      // Calcola la posizione relativa della sezione rispetto alla viewport
      // Quando la sezione entra dal basso: scrollProgress negativo
      // Quando la sezione esce dall'alto: scrollProgress positivo
      const scrollProgress = -rect.top / (windowHeight + rect.height);

      // Applica l'effetto parallasse
      this.applyParallax(scrollProgress);
    }
  }

  applyParallax(progress) {
    // Questa funzione viene chiamata SOLO se enableParallax === true
    if (this.media) {
      // Effetto parallasse MARCATO: l'immagine/video si muove nella DIREZIONE OPPOSTA allo scroll
      // Quando scrolli GIÙ (testi salgono), l'immagine SCENDE
      // Movimento molto più evidente: da 0% a +40%
      // Quando la sezione entra: 0% (posizione iniziale)
      // Quando scrolli giù: +40% (si muove marcatamente verso il basso, creando parallasse evidente)
      const parallaxAmount = 40; // Movimento marcato verso il basso (aumentato da 15 a 40)
      const translateY = progress * parallaxAmount;

      // Applica la trasformazione
      this.media.style.transform = `translateY(${translateY}%)`;
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize all editorial box sections
function initEditorialBoxes() {
  const sections = document.querySelectorAll('.editorial-box-cova');
  sections.forEach(section => {
    // Evita di inizializzare più volte
    if (!section.dataset.initialized) {
      new EditorialBoxCova(section);
      section.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEditorialBoxes);
} else {
  initEditorialBoxes();
}

// Shopify theme editor support
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.editorial-box-cova');
    if (section && !section.dataset.initialized) {
      new EditorialBoxCova(section);
      section.dataset.initialized = 'true';
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.editorial-box-cova');
    if (section) {
      section.dataset.initialized = 'false';
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.querySelector('.editorial-box-cova');
    if (section) {
      // Scroll to section when selected in editor
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}

